import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { sidebarNavigation, type NavItem } from "@alm/policy";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RecordCard } from "@/components/app/lists/record-card";
import { EmptyRows } from "@/components/app/lists/empty-rows";
import { Skeleton } from "@/components/ui/skeleton";
import { arrangeMenu, useMenuPreferences, type MenuPreference } from "@/lib/workspace/menu";
import { orpc } from "@/lib/orpc";
import { usePolicy } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { MenuPreferenceRows, type MenuRow } from "./-components/account/menu-preference-rows";

export const Route = createFileRoute("/_authenticated/_administration/settings/menu")({
  beforeLoad: ({ context }) => guardRoute(context.me, "menu"),
  // Reached from the rail's pin as often as from the hub: the person's own
  // menu wants the width, not the hub's list beside it.
  staticData: { fullWidth: true },
  component: MenuSettings,
});

/** The dashboard leads the rail and does not move; everything else is the reader's to arrange. */
function toRows(rail: readonly NavItem[], preferences: readonly MenuPreference[]): MenuRow[] {
  const arranged = arrangeMenu(rail, preferences).map(({ entry, isVisible, isPinned }) => ({
    entry,
    isVisible,
    isPinned,
  }));
  return [
    ...arranged.filter((row) => row.entry.id === "dashboard"),
    ...arranged.filter((row) => row.entry.id !== "dashboard"),
  ];
}

/** What the sidebar shows you, and in what order. */
function MenuSettings() {
  const { navigation } = usePolicy();
  const rail = useMemo(() => sidebarNavigation(navigation), [navigation]);
  const preferences = useMenuPreferences();
  const queryClient = useQueryClient();
  // Null until this person moves something; every change saves at once, and a
  // save that fails puts the rows back the way the server has them. Once the last
  // save lands the server's rows are shown again, so a building switch is followed.
  const [draft, setDraft] = useState<MenuRow[] | null>(null);
  const latest = useRef(0);

  const save = useMutation(
    orpc.administration.menu.save.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: orpc.administration.menu.key() });
      },
      onError: (error) => {
        toast.error(errorWords(error));
        setDraft(null);
      },
    }),
  );
  const commit = (next: MenuRow[]) => {
    latest.current += 1;
    const turn = latest.current;
    setDraft(next);
    save.mutate(
      {
        preferences: next.map((row, index) => ({
          menuItemId: row.entry.id,
          isVisible: row.isVisible,
          isPinned: row.isPinned,
          sortOrder: index,
        })),
      },
      { onSuccess: () => (turn === latest.current ? setDraft(null) : undefined) },
    );
  };

  const header = (
    <PageHeader
      title="Navigation & favorites"
      meta="Hide what you never open, pin what you always do, and put the rest in your own order. Every change saves as you make it."
      actions={
        <span className="text-muted-foreground text-sm" aria-live="polite">
          {save.isPending ? "Saving…" : save.isSuccess ? "Saved" : null}
        </span>
      }
    />
  );
  if (preferences.isPending) {
    return (
      <>
        {header}
        <Skeleton className="h-96 w-full" />
      </>
    );
  }
  if (preferences.isError) {
    // Rendering the defaults as though they were the saved arrangement would
    // make the first Save overwrite the real one with them.
    return (
      <>
        {header}
        <ReadFailed
          message="Could not load your menu."
          error={preferences.error}
          retrying={preferences.isFetching}
          onRetry={() => void preferences.refetch()}
        />
      </>
    );
  }

  const rows = draft ?? toRows(rail, preferences.data);

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    // Index 0 is the dashboard, which nothing may displace.
    if (target < 1 || target >= rows.length) return;
    const next = [...rows];
    const moved = next[index];
    const displaced = next[target];
    if (!moved || !displaced) return;
    next[index] = displaced;
    next[target] = moved;
    commit(next);
  };

  return (
    <>
      {header}
      {/* Capped so a row's name and its switches stay within one glance. */}
      <div className="max-w-3xl">
        <RecordCard title="Your menu" count={rows.length} canWrite={false}>
          {rows.length === 0 ? (
            <EmptyRows text="Nothing in the sidebar for this account." />
          ) : (
            <MenuPreferenceRows
              rows={rows}
              onMove={move}
              onToggleVisible={(id, isVisible) =>
                commit(
                  rows.map((row) =>
                    row.entry.id === id
                      ? // Hiding an entry unpins it: a pinned row that is not shown
                        // would be an instruction the rail cannot carry out.
                        { ...row, isVisible, isPinned: isVisible ? row.isPinned : false }
                      : row,
                  ),
                )
              }
              onTogglePinned={(id, isPinned) =>
                commit(rows.map((row) => (row.entry.id === id ? { ...row, isPinned } : row)))
              }
            />
          )}
        </RecordCard>
      </div>
    </>
  );
}
